import React, { useState, useEffect } from 'react';
import { Button, Typography, Space, Card, Tooltip } from 'antd';
import { CopyOutlined, FileTextOutlined } from '@ant-design/icons';
import { renderToStaticMarkup } from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Languages } from './Data';
import { I18 } from '../i18/i18';

const { Title } = Typography;

const MarkdownPage = () => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [rawContent, setRawContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [language, setLanguage] = useState('EN');

  // Function to convert data to markdown format
  const convertToMarkdown = (data) => {
    const lines = [];

    // Header
    lines.push('# ' + data.about.name + ' - Professional Profile');
    lines.push('');
    lines.push('---');
    lines.push('');

    // Header Information
    lines.push('## Header Information');
    lines.push('');

    // Personal Details
    lines.push('### Personal Details');
    lines.push(`- **Name:** ${data.about.name}`);
    if (!data.about.hidePhone) {
      lines.push(`- **Phone:** ${data.about.phone}`);
    }
    //if (!data.about.hideEmail) {
    lines.push(`- **Email:** ${data.about.email}`);
    //}
    lines.push(`- **Address:** ${data.about.address}`);
    lines.push(`- **University:** ${data.about.university}`);
    lines.push('');

    // Taglines
    lines.push('### Taglines');
    data.about.taglines.forEach(tagline => {
      lines.push(`- ${tagline}`);
    });
    lines.push('');

    // Languages
    lines.push('### Languages');
    data.about.languages.forEach(lang => {
      lines.push(`- **${lang.name}:** ${lang.level}`);
    });
    lines.push('');

    // Social Links
    lines.push('### Social Links');
    data.socialLinks.forEach(link => {
      lines.push(`- **${link.title}:** ${link.link}`);
    });
    lines.push('');
    lines.push('---');
    lines.push('');

    // About
    lines.push('## About');
    lines.push('');
    const aboutDetails = renderToStaticMarkup(data.about.details);
    // const aboutDetails = extractTextFromReactFragment(data.about.details);
    lines.push(aboutDetails);
    lines.push('');
    lines.push('---');
    lines.push('');

    // Soft Skills
    lines.push('## Soft Skills');
    lines.push('');
    const softSkills = data.skills.filter(s => s.type === 'soft');
    // softSkills.forEach(skill => {
    //   lines.push(`- ${skill.title}`);
    // });
    lines.push('|  |  |');
    lines.push('|-------|----------------------|');

    let accumulate = "";
    softSkills.forEach((skill, ix) => {
      if (ix % 2 == 0) accumulate = `| ✓ ${skill.title} | `
      else {
        lines.push(`${accumulate}  ✓ ${skill.title} |`);
        accumulate = "";
      }

    });
    lines.push('');
    lines.push('---');
    lines.push('');

    // Hard Skills
    lines.push('## Hard Skills');
    lines.push('');
    const hardSkills = data.skills.filter(s => s.type === 'hard');
    lines.push('### Programming Languages & Frameworks');
    lines.push('');
    lines.push('| Skill | Qualification (1-10) |');
    lines.push('|-------|----------------------|');
    const programmingSkills = hardSkills.filter(s => s.subtype == "programming");
    programmingSkills.forEach(skill => {
      lines.push(`| ${skill.title} | ${String(skill.skillQualification).padStart(2, '0')} - ${"⭐".repeat(skill.skillQualification)} ${"☆".repeat(10 - skill.skillQualification)} |`);
    });
    lines.push('');

    lines.push('### Databases');
    lines.push('');
    lines.push('| Skill | Qualification (1-10) |');
    lines.push('|-------|----------------------|');
    const dbSkills = hardSkills.filter(s => s.subtype == "database");
    dbSkills.forEach(skill => {
      lines.push(`| ${skill.title} | ${String(skill.skillQualification).padStart(2, '0')} - ${"⭐".repeat(skill.skillQualification)} ${"☆".repeat(10 - skill.skillQualification)} |`);
    });
    lines.push('');

    lines.push('### Tools & Platforms');
    lines.push('');
    lines.push('| Skill | Qualification (1-10) |');
    lines.push('|-------|----------------------|');
    const toolSkills = hardSkills.filter(s => s.subtype == "tool-platform");
    toolSkills.forEach(skill => {
      lines.push(`| ${skill.title} | ${String(skill.skillQualification).padStart(2, '0')} - ${"⭐".repeat(skill.skillQualification)} ${"☆".repeat(10 - skill.skillQualification)} |`);
    });
    lines.push('');
    lines.push('---');
    lines.push('');

    // Education
    lines.push('## Education');
    lines.push('');
    data.education.filter(i => i.display != false).forEach(edu => {
      lines.push(`### ${edu.category}`);
      lines.push('');
      lines.push(`#### ${edu.institution}`);
      lines.push(`- **Degree:** ${edu.degree}`);
      lines.push(`- **Period:** ${edu.from} - ${edu.to}`);
      if (edu.certificate && edu.certificate.length > 0) {
        lines.push(`- **Certificate:** ${edu.certificate.map(c => `${c.title}: ${c.value}`).join(', ')}`);
      }
      lines.push('');
    });
    lines.push('---');
    lines.push('');

    // Professional Experience
    lines.push('## Professional Experience');
    lines.push('');
    data.experiences.forEach(exp => {
      lines.push(`### ${exp.company}`);
      lines.push(`- **Position:** ${exp.position}`);
      lines.push(`- **Period:** ${exp.from} - ${exp.to}`);
      lines.push('');
      lines.push('#### Responsibilities:');
      exp.responsibilities.forEach(resp => {
        lines.push(`- ${resp}`);
      });
      lines.push('');
      lines.push('#### Tech Stack:');
      exp.techStack.forEach(tech => {
        lines.push(`- ${tech}`);
      });
      lines.push('');
      lines.push('---');
      lines.push('');
    });

    // Projects - Group by category
    const showProjects = false
    if (showProjects) {
      lines.push('## Projects');
      lines.push('');
      const npmProjects = data.projects.filter(p => p.categories && p.categories.includes('Package'));
      const webProjects = data.projects.filter(p => p.categories && p.categories.includes('Web site'));
      const otherProjects = data.projects.filter(p => p.categories && p.categories.includes('Other'));
      const dotnetProjects = data.projects.filter(p => p.categories && p.categories.includes('.NET'));
      const vscodeProjects = data.projects.filter(p => p.categories && p.categories.includes('VS Code'));
      const libraryProjects = data.projects.filter(p => p.categories && p.categories.includes('Librería'));

      if (npmProjects.length > 0 || libraryProjects.length > 0) {
        lines.push('### NPM Packages & Libraries');
        lines.push('');
        [...npmProjects, ...libraryProjects].forEach(proj => {
          lines.push(`#### ${proj.title}`);
          lines.push(`- **Categories:** ${proj.categories ? proj.categories.join(', ') : 'N/A'}`);
          lines.push(`- **Description:** ${extractTextFromReactFragment(proj.description)}`);
          if (proj.skills && proj.skills.length > 0) {
            lines.push('- **Skills:**');
            proj.skills.forEach(skill => {
              lines.push(`  - ${skill.title}`);
            });
          }
          if (proj.links && proj.links.length > 0) {
            lines.push('- **Links:**');
            proj.links.forEach(link => {
              lines.push(`  - [${link.title}](${link.url})`);
            });
          }
          lines.push('');
        });
      }

      if (webProjects.length > 0) {
        lines.push('### Web Applications');
        lines.push('');
        webProjects.forEach(proj => {
          lines.push(`#### ${proj.title}`);
          lines.push(`- **Categories:** ${proj.categories ? proj.categories.join(', ') : 'N/A'}`);
          lines.push(`- **Description:** ${extractTextFromReactFragment(proj.description)}`);
          if (proj.skills && proj.skills.length > 0) {
            lines.push('- **Skills:**');
            proj.skills.forEach(skill => {
              lines.push(`  - ${skill.title}`);
            });
          }
          if (proj.links && proj.links.length > 0) {
            lines.push('- **Links:**');
            proj.links.forEach(link => {
              lines.push(`  - [${link.title}](${link.url})`);
            });
          }
          lines.push('');
        });
      }

      if (otherProjects.length > 0) {
        lines.push('### Other Projects');
        lines.push('');
        otherProjects.forEach(proj => {
          lines.push(`#### ${proj.title}`);
          lines.push(`- **Categories:** ${proj.categories ? proj.categories.join(', ') : 'N/A'}`);
          lines.push(`- **Description:** ${extractTextFromReactFragment(proj.description)}`);
          if (proj.links && proj.links.length > 0) {
            lines.push('- **Links:**');
            proj.links.forEach(link => {
              lines.push(`  - [${link.title}](${link.url})`);
            });
          }
          lines.push('');
        });
      }

      if (dotnetProjects.length > 0) {
        lines.push('### .NET Projects');
        lines.push('');
        dotnetProjects.forEach(proj => {
          lines.push(`#### ${proj.title}`);
          lines.push(`- **Categories:** ${proj.categories ? proj.categories.join(', ') : 'N/A'}`);
          lines.push(`- **Description:** ${extractTextFromReactFragment(proj.description)}`);
          if (proj.links && proj.links.length > 0) {
            lines.push('- **Links:**');
            proj.links.forEach(link => {
              lines.push(`  - [${link.title}](${link.url})`);
            });
          }
          lines.push('');
        });
      }

      if (vscodeProjects.length > 0) {
        lines.push('### VS Code Extensions');
        lines.push('');
        vscodeProjects.forEach(proj => {
          lines.push(`#### ${proj.title}`);
          lines.push(`- **Categories:** ${proj.categories ? proj.categories.join(', ') : 'N/A'}`);
          lines.push(`- **Description:** ${extractTextFromReactFragment(proj.description)}`);
          if (proj.links && proj.links.length > 0) {
            lines.push('- **Links:**');
            proj.links.forEach(link => {
              lines.push(`  - [${link.title}](${link.url})`);
            });
          }
          lines.push('');
        });
      }

      lines.push('---');
      lines.push('');

    }

    // References
    const showReferences = false;
    if (showReferences) {
      if (data.references && data.references.length > 0) {
        lines.push('## References');
        lines.push('');
        data.references.forEach(ref => {
          lines.push(`### ${ref.company || ref.name}`);
          lines.push(`- **Position:** ${ref.position}`);
          lines.push(`- **Period:** ${ref.from} - ${ref.to}`);
          lines.push(`- **Contact:** ${ref.contact}`);
          lines.push('');
        });
        lines.push('---');
        lines.push('');
      }
    }



    return lines.join('\n');
  };

  // Helper function to extract text from React Fragment
  const extractTextFromReactFragment = (element) => {
    if (!element) return '';
    if (typeof element === 'string') return element;
    if (Array.isArray(element)) {
      return element.map(extractTextFromReactFragment).join('');
    }
    if (element && element.props && element.props.children) {
      return extractTextFromReactFragment(element.props.children);
    }
    return '';
  };

  useEffect(() => {
    const data = Languages[language];
    const markdown = convertToMarkdown(data);
    setMarkdownContent(markdown);
    setRawContent(markdown);
    setLoading(false);
  }, [language]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(rawContent)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  const downloadAsFile = () => {
    const blob = new Blob([rawContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'luis-arias-profile.md';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'EN' ? 'ES' : 'EN');
    setLoading(true);
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2}>Loading Markdown Content...</Title>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card>
          <Space direction="horizontal" size="middle" wrap>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              Markdown Profile Viewer
            </Title>
            <Button onClick={toggleLanguage} style={{ marginLeft: 'auto' }}>
              Switch to {language === 'EN' ? 'Spanish' : 'English'}
            </Button>
          </Space>

        </Card>

        <Space direction="horizontal" size="middle" wrap>
          <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={copyToClipboard}
            >
              {copied ? 'Copied!' : 'Copy Raw Markdown'}
            </Button>
          </Tooltip>
          <Button
            icon={<FileTextOutlined />}
            onClick={downloadAsFile}
          >
            Download as .md File
          </Button>
        </Space>

        <Card>
          <div style={{
            backgroundColor: '#f6f8fa',
            padding: '16px',
            borderRadius: '4px',
            overflowX: 'auto'
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                h1: ({ ...props }) => <Title level={1} {...props} />,
                h2: ({ ...props }) => <Title level={2} {...props} />,
                h3: ({ ...props }) => <Title level={3} {...props} />,
                h4: ({ ...props }) => <Title level={4} {...props} />,
                h5: ({ ...props }) => <Title level={5} {...props} />,
                p: ({ ...props }) => <Typography.Paragraph {...props} />,
                a: ({ children, href, ...props }) => {
                  if (!href) {
                    return <span style={{ color: '#1890ff' }}>{children}</span>;
                  }
                  const isExternal = href.startsWith('http');
                  const linkProps = {
                    ...props,
                    href,
                    style: { color: '#1890ff' },
                    ...(isExternal && { target: '_blank', rel: 'noopener noreferrer' }),
                  };
                  return (
                    <a {...linkProps}>
                      {children || href}
                    </a>
                  );
                },
                table: ({ ...props }) => (
                  <table
                    style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                      margin: '16px 0',
                      backgroundColor: 'white'
                    }}
                    {...props}
                  />
                ),
                th: ({ ...props }) => (
                  <th
                    style={{
                      border: '1px solid #e8e8e8',
                      padding: '12px',
                      textAlign: 'left',
                      backgroundColor: '#fafafa',
                      fontWeight: 'bold'
                    }}
                    {...props}
                  />
                ),
                td: ({ ...props }) => (
                  <td
                    style={{
                      border: '1px solid #e8e8e8',
                      padding: '12px',
                      textAlign: 'left'
                    }}
                    {...props}
                  />
                ),
                ul: ({ ...props }) => <ul style={{ paddingLeft: '24px' }} {...props} />,
                ol: ({ ...props }) => <ol style={{ paddingLeft: '24px' }} {...props} />,
                li: ({ ...props }) => <li style={{ margin: '4px 0' }} {...props} />,
                blockquote: ({ ...props }) => (
                  <blockquote
                    style={{
                      borderLeft: '4px solid #1890ff',
                      paddingLeft: '16px',
                      margin: '16px 0',
                      color: '#666'
                    }}
                    {...props}
                  />
                ),
                code: ({ ...props }) => (
                  <code
                    style={{
                      backgroundColor: '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontFamily: 'monospace',
                      fontSize: '0.9em'
                    }}
                    {...props}
                  />
                ),
                pre: ({ ...props }) => (
                  <pre
                    style={{
                      backgroundColor: '#272822',
                      padding: '16px',
                      borderRadius: '4px',
                      overflowX: 'auto',
                      margin: '16px 0'
                    }}
                    {...props}
                  />
                ),
                hr: ({ ...props }) => (
                  <hr
                    style={{
                      border: 'none',
                      borderTop: '1px solid #e8e8e8',
                      margin: '24px 0'
                    }}
                    {...props}
                  />
                ),
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </div>
        </Card>

        <Card title="Raw Markdown Content">
          <div style={{
            backgroundColor: '#272822',
            color: '#f8f8f2',
            padding: '16px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '0.85em',
            whiteSpace: 'pre-wrap',
            overflowX: 'auto',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            {rawContent}
          </div>
        </Card>
      </Space>
    </div>
  );
};

export default MarkdownPage;
